import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { trackEvent } from '@/lib/analytics';
import {
  Trophy,
  Users,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Bomb,
  Search,
} from 'lucide-react';

interface JoinLeaguePageProps {
  inviteCode?: string;
  onJoin: (leagueId: string) => void;
  onCancel: () => void;
}

interface LeagueSearchResult {
  id: string;
  name: string;
  invite_code: string;
  team_count: number;
  scoring_type: string;
  sabotage_enabled: boolean;
  sport: string;
  commissioner_name?: string;
  filled_spots: number;
  faab_budget?: number;
  waiver_type?: string;
}

const JoinLeaguePage = ({ inviteCode, onJoin, onCancel }: JoinLeaguePageProps) => {
  const [code, setCode] = useState(inviteCode || '');
  const [searchResult, setSearchResult] = useState<LeagueSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [joining, setJoining] = useState(false);
  const [teamName, setTeamName] = useState('');
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSearch = async () => {
    const normalizedCode = code
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .trim();

    if (!normalizedCode) return;
    setCode(normalizedCode);
    setSearching(true);
    setSearchResult(null);

    try {
      // Find league by invite code using secure RPC
      const { data: leagueResults, error } = await supabase
        .rpc('find_league_by_invite_code', { code: normalizedCode });

      if (error) throw error;

      const league = Array.isArray(leagueResults) ? leagueResults[0] : leagueResults;

      if (!league) {
        toast({
          title: "League Not Found",
          description: "No league found with that invite code. Please check and try again.",
          variant: "destructive",
        });
        return;
      }

      // Get member count
      const { count } = await supabase
        .from('league_members')
        .select('*', { count: 'exact', head: true })
        .eq('league_id', league.id);

      // Get commissioner name via RPC.
      // SAB-892: the 1-arg form is no longer anon-executable -- it mapped any league_id to
      // that league's commissioner for unauthenticated callers. This page is mounted on the
      // public landing page, so it passes the invite code the user has already supplied and
      // which find_league_by_invite_code above has just validated. `normalizedCode` is the
      // uppercased form; the function uppercases too, matching find_league_by_invite_code.
      const { data: commissionerName } = await (supabase.rpc as any)('get_commissioner_name', {
        league_id: league.id,
        invite_code: normalizedCode,
      });

      setSearchResult({
        id: league.id,
        name: league.name,
        invite_code: league.invite_code,
        team_count: league.team_count,
        scoring_type: league.scoring_type,
        sabotage_enabled: league.sabotage_enabled,
        sport: league.sport,
        commissioner_name: commissionerName || 'Unknown',
        filled_spots: count || 0,
        faab_budget: league.faab_budget,
        waiver_type: league.waiver_type,
      });
    } catch (error: any) {
      toast({
        title: "Search failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSearching(false);
    }
  };

  // SAB-642: when mounted from the /beta/join/:code deep link, auto-look up the code so the
  // invitee sees the league immediately (parity with NFLJoinLeaguePage's auto-lookup), rather
  // than landing on an empty search box after coming through the invite + signup.
  useEffect(() => {
    if (inviteCode) void handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCode]);

  const handleJoin = async (league: LeagueSearchResult) => {
    if (!user) {
      console.error('[JoinLeague] No authenticated user - cannot join');
      toast({ title: "Not signed in", description: "Please sign in to join a league.", variant: "destructive" });
      return;
    }
    console.log('[JoinLeague] Attempting join:', { leagueId: league.id, userId: user.id, teamName });
    setJoining(true);

    try {
      // Check if already a member
      const { data: existing } = await supabase
        .from('league_members')
        .select('id')
        .eq('league_id', league.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing) {
        console.log('[JoinLeague] Already a member:', existing);
        toast({ title: "Already a member", description: "You're already in this league!", variant: "destructive" });
        setJoining(false);
        return;
      }

      // Check if league is full
      if (league.filled_spots >= league.team_count) {
        console.log('[JoinLeague] League full:', league.filled_spots, '/', league.team_count);
        toast({ title: "League full", description: "This league has no open spots.", variant: "destructive" });
        setJoining(false);
        return;
      }

      // Get league faab budget via RPC (already have from search result)
      const leagueData = { faab_budget: league.faab_budget, waiver_type: league.waiver_type } as any;
      if (!leagueData.faab_budget) {
        const { data: rpcResult } = await supabase.rpc('find_league_by_invite_code', { code: league.invite_code });
        const rpcLeague = Array.isArray(rpcResult) ? rpcResult[0] : rpcResult;
        if (rpcLeague) {
          leagueData.faab_budget = rpcLeague.faab_budget;
          leagueData.waiver_type = rpcLeague.waiver_type;
        }
      }

      // Create team
      const finalTeamName = teamName.trim() || `${user.user_metadata?.full_name || 'My'}'s Team`;
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .insert({
          league_id: league.id,
          user_id: user.id,
          name: finalTeamName,
          faab_remaining: leagueData?.waiver_type?.startsWith('faab') ? leagueData.faab_budget : null,
          waiver_priority: league.filled_spots + 1,
        })
        .select()
        .single();

      if (teamError) {
        console.error('[JoinLeague] Team creation failed:', teamError);
        throw teamError;
      }
      console.log('[JoinLeague] Team created:', team.id);

      // Add as league member
      const { error: memberError } = await supabase
        .from('league_members')
        .insert({
          league_id: league.id,
          user_id: user.id,
          team_id: team.id,
          role: 'player',
        });

      if (memberError) throw memberError;

      trackEvent('league_joined', { league_id: league.id, league_name: league.name });
      toast({
        title: "Joined Successfully! 🎉",
        description: `Welcome to ${league.name}!`,
      });
      onJoin(league.id);
    } catch (error: any) {
      console.error('Join error:', error);
      toast({
        title: "Failed to join",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold">Join League</h1>
        <p className="text-muted-foreground">Enter an invite code to find and join a league</p>
      </div>

      {/* Invite Code Search */}
      <Card>
        <CardHeader>
          <CardTitle>Have an Invite Code?</CardTitle>
          <CardDescription>
            Enter the code shared by your league commissioner
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Enter invite code..."
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                className="pl-9 font-mono uppercase"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button onClick={handleSearch} disabled={!code || searching}>
              {searching ? 'Searching...' : 'Find League'}
            </Button>
          </div>

          {/* Search Result */}
          {searchResult && (
            <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-display font-bold text-lg">{searchResult.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    Created by {searchResult.commissioner_name} • {searchResult.sport.toUpperCase()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-2 bg-background/50 rounded">
                  <Users className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-sm font-medium">{searchResult.filled_spots}/{searchResult.team_count}</p>
                  <p className="text-xs text-muted-foreground">Teams</p>
                </div>
                <div className="text-center p-2 bg-background/50 rounded">
                  <Trophy className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-sm font-medium capitalize">{searchResult.scoring_type}</p>
                  <p className="text-xs text-muted-foreground">Scoring</p>
                </div>
                <div className="text-center p-2 bg-background/50 rounded">
                  <Bomb className="w-4 h-4 mx-auto mb-1 text-primary" />
                  <p className="text-sm font-medium">{searchResult.sabotage_enabled ? 'Enabled' : 'Disabled'}</p>
                  <p className="text-xs text-muted-foreground">Sabotages</p>
                </div>
              </div>

              {/* Team Name Input */}
              <div className="space-y-2 mb-4">
                <label className="text-sm font-medium">Your Team Name</label>
                <Input
                  placeholder={`${user?.user_metadata?.full_name || 'My'}'s Team`}
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  maxLength={30}
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={() => handleJoin(searchResult)} className="flex-1" disabled={joining}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {joining ? 'Joining...' : 'Join League'}
                </Button>
                <Button variant="outline" onClick={() => { setSearchResult(null); setCode(''); }}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Back Button */}
      <Button variant="outline" onClick={onCancel}>
        Back to Home
      </Button>
    </div>
  );
};

export default JoinLeaguePage;
